import { Component, ViewChild, ElementRef, NgZone, ChangeDetectorRef } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap';
import { SecurityService } from '../services/security.service';
import { StorageService } from '../services/storage.service';
import { UserPreferencesService } from '../services/user-preferences.service';
import { CASE_COLUMNS_CATALOG, CaseColumnDef, DEFAULT_VISIBLE_COLUMN_NAMES } from '../beans/case-columns-catalog';

@Component({
	selector: 'app-preferences',
	templateUrl: './preferences.component.html',
	styleUrls: ['./preferences.component.scss']
})
export class PreferencesComponent {

	@ViewChild('modalRef') modalRef: ModalDirective;
	@ViewChild('picker') pickerRef: ElementRef;

	activeTab: 'color' | 'columns' = 'color';

	// Цвят
	selectedColor = '#FFFFFF';

	// Колони в Дела
	columnsCatalog: CaseColumnDef[] = CASE_COLUMNS_CATALOG;
	columnVisibility: { [name: string]: boolean } = {};
	// Подреден списък на ВИДИМИТЕ колони — позицията в този array = позицията
	// в списъка с дела. Видимите се рендерират горе с ↑/↓ стрелки; скритите
	// долу в реда на каталога (без приоритет — стават „видими" с клик и отиват
	// най-долу в видимите).
	visibleOrder: string[] = [];

	// Съобщения
	message = '';
	error = '';
	saving = false;

	constructor(
		private securityService: SecurityService,
		private storageService: StorageService,
		private prefsService: UserPreferencesService,
		private zone: NgZone,
		private cdr: ChangeDetectorRef
	) { }

	show() {
		this.message = '';
		this.error = '';
		this.saving = false; // reset за случая, в който предишен save е заседнал

		// Цвят — четем от LoginResponse
		const lr = this.storageService.getLoginResponse();
		this.selectedColor = (lr && lr.color) || '#FFFFFF';

		// Колони — от prefs
		this.prefsService.load().subscribe(prefs => {
			const visible = (prefs && prefs.cases && prefs.cases.visibleColumns)
				|| DEFAULT_VISIBLE_COLUMN_NAMES;
			const set = new Set(visible);
			this.columnVisibility = {};
			for (const c of this.columnsCatalog) {
				this.columnVisibility[c.name] = set.has(c.name);
			}
			// Запазваме реда от prefs (само за колони, които още съществуват в каталога).
			const catalogNames = new Set(this.columnsCatalog.map(c => c.name));
			this.visibleOrder = visible.filter(n => catalogNames.has(n));
		});

		this.modalRef.show();

		setTimeout(() => {
			if (this.pickerRef) {
				this.pickerRef.nativeElement.value = this.selectedColor;
				this.attachPickerListeners();
			}
		}, 100);
	}

	private attachPickerListeners() {
		if (!this.pickerRef) return;
		const el = this.pickerRef.nativeElement as HTMLInputElement;
		const handler = (e: any) => {
			this.zone.run(() => {
				this.selectedColor = e.target.value;
				this.cdr.detectChanges();
			});
		};
		el.addEventListener('input', handler);
		el.addEventListener('change', handler);
	}

	switchTab(tab: 'color' | 'columns') {
		this.activeTab = tab;
		this.message = '';
		this.error = '';
	}

	toggleColumn(name: string) {
		const nowVisible = !this.columnVisibility[name];
		this.columnVisibility[name] = nowVisible;
		if (nowVisible) {
			// При включване → отива най-долу в видимите (default приоритет: най-нисък).
			if (this.visibleOrder.indexOf(name) === -1) {
				this.visibleOrder.push(name);
			}
		} else {
			this.visibleOrder = this.visibleOrder.filter(n => n !== name);
		}
	}

	selectAllColumns() {
		this.visibleOrder = [];
		for (const c of this.columnsCatalog) {
			this.columnVisibility[c.name] = true;
			this.visibleOrder.push(c.name);
		}
	}

	deselectAllColumns() {
		for (const c of this.columnsCatalog) {
			this.columnVisibility[c.name] = false;
		}
		this.visibleOrder = [];
	}

	/** Помощници за UI — гарантирано връщат CaseColumnDef-и. */
	get visibleDefs(): CaseColumnDef[] {
		const byName: { [n: string]: CaseColumnDef } = {};
		for (const c of this.columnsCatalog) {
			byName[c.name] = c;
		}
		return this.visibleOrder.map(n => byName[n]).filter(d => !!d);
	}

	get hiddenDefs(): CaseColumnDef[] {
		const visibleSet = new Set(this.visibleOrder);
		return this.columnsCatalog.filter(c => !visibleSet.has(c.name));
	}

	moveUp(index: number) {
		if (index <= 0) { return; }
		const arr = this.visibleOrder;
		[arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
	}

	moveDown(index: number) {
		const arr = this.visibleOrder;
		if (index >= arr.length - 1) { return; }
		[arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
	}

	save() {
		if (this.saving) return;
		this.saving = true;
		this.message = '';
		this.error = '';

		// 1) Цвят (само ако е променен)
		const lr = this.storageService.getLoginResponse();
		const prevColor = (lr && lr.color) || '#FFFFFF';
		const colorChanged = this.selectedColor !== prevColor;

		// 2) Колони — записваме РЕДА (приоритета) от visibleOrder, а не каталога.
		const visibleColumns = this.visibleOrder.slice();

		const tasks: Promise<any>[] = [];

		if (colorChanged) {
			tasks.push(
				this.securityService.changeOwnColor(this.selectedColor).toPromise().then(() => {
					if (lr) {
						lr.color = this.selectedColor;
						this.storageService.setLoginResponse(lr);
					}
				})
			);
		}

		tasks.push(
			this.prefsService.patch({ cases: { visibleColumns } }).toPromise()
		);

		Promise.all(tasks).then(() => {
			this.saving = false;
			this.message = 'Настройките са запазени';
			if (colorChanged) {
				// Презареждаме страницата, за да приложим новия цвят навсякъде
				setTimeout(() => window.location.reload(), 600);
			} else {
				setTimeout(() => this.modalRef.hide(), 600);
			}
		}).catch((err) => {
			this.saving = false;
			this.error = (err && err.error && err.error.message) || 'Грешка при запис';
		});
	}

	cancel() {
		this.modalRef.hide();
	}
}
